import {
  Button,
  Card,
  Descriptions,
  Image,
  Spin,
  Table,
  Tag,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import DataProvider from "../../apis";
import type {
  DoctorPrescriptionItem,
  DoctorPrescriptionOrderDetailItem,
} from "../../apis";
import Colors from "../../constants/Colors";
import {
  doctorPrescriptionStatusConfig,
  E_PRESCRIPTIONS_BASE,
} from "../../constants/e-prescriptions";

const medicationColumns: ColumnsType<DoctorPrescriptionOrderDetailItem> = [
  {
    title: "Medicine",
    dataIndex: "drugName",
    key: "drugName",
    ellipsis: true,
  },
  {
    title: "Dose / strength",
    dataIndex: "instructions",
    key: "instructions",
    width: 120,
    ellipsis: true,
    render: (v: string) =>
      v ? v.replace(/^Dose\/strength:\s*/i, "").trim() || "—" : "—",
  },
  {
    title: "Route",
    dataIndex: "route",
    key: "route",
    width: 100,
    render: (v: string) => v ?? "—",
  },
  {
    title: "Dosage",
    dataIndex: "dosage",
    key: "dosage",
    ellipsis: true,
    render: (v: string) => v ?? "—",
  },
  {
    title: "Duration",
    dataIndex: "duration",
    key: "duration",
    width: 110,
    render: (v: string) => v ?? "—",
  },
  {
    title: "Qty",
    dataIndex: "quantity",
    key: "quantity",
    width: 56,
  },
  {
    title: "Unit price",
    dataIndex: "unitPrice",
    key: "unitPrice",
    width: 96,
    render: (v: number) =>
      typeof v === "number" ? v.toFixed(2) : "—",
  },
];

export default function EPrescriptionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<DoctorPrescriptionItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    DataProvider.getDoctorPrescriptionById(Number(id))
      .then((res) => setData(res.data as DoctorPrescriptionItem))
      .catch((err) => {
        setError(
          err?.response?.data?.message ?? "Could not load this prescription."
        );
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh] bg-[#f8fafc]">
        <Spin size="large" tip="Loading prescription…" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-[50vh] bg-[#f8fafc] flex flex-col items-center justify-center p-6">
        <p className="text-red-600 mb-4 text-center">{error ?? "Prescription not found."}</p>
        <Button
          type="primary"
          onClick={() => navigate(E_PRESCRIPTIONS_BASE)}
          style={{ background: Colors.primary }}
        >
          Back to prescriptions
        </Button>
      </div>
    );
  }

  const status = doctorPrescriptionStatusConfig[data.status] ?? {
    color: "#94a3b8",
    label: data.status?.replace(/_/g, " ") ?? "—",
    servedHint: undefined,
  };

  // The suggested filename needs its own `.pdf` — Cloudinary's
  // fl_attachment flag controls the Content-Disposition filename
  // independently of the underlying resource's own extension, and without
  // it here the downloaded file has no extension for the OS/other apps to
  // identify as a PDF (same root cause as the resource itself needing one —
  // see doctor-prescription.service.ts's upload call).
  const pdfDownloadUrl = data.pdfUrl
    ? data.pdfUrl.replace(
        "/upload/",
        `/upload/fl_attachment:prescription_${data.orderId}.pdf/`
      )
    : null;

  return (
    <div className="min-h-[70vh] bg-[#f8fafc] pb-12">
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(E_PRESCRIPTIONS_BASE)}
            className="-ml-1 text-gray-600 hover:text-gray-900"
            aria-label="Back to prescriptions"
          >
            Back
          </Button>
          {data.pdfUrl && (
            <div className="flex items-center gap-2">
              <Button
                icon={<EyeOutlined />}
                href={data.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View prescription PDF"
              >
                View PDF
              </Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                href={pdfDownloadUrl ?? undefined}
                rel="noopener noreferrer"
                style={{ background: Colors.primary }}
                aria-label="Download prescription PDF"
              >
                Download PDF
              </Button>
            </div>
          )}
        </div>

        <Card
          className="rounded-xl shadow-sm border-gray-100 overflow-hidden"
          styles={{ body: { padding: 0 } }}
        >
          {/* Order header */}
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-gray-500 m-0">Order reference</p>
                <p className="text-lg font-bold text-gray-900 m-0 font-mono">
                  #{data.orderId}
                </p>
              </div>
              <Tag color={status.color} className="text-sm rounded-full px-3">
                {status.label}
              </Tag>
            </div>
            {status.servedHint && (
              <p className="text-sm text-gray-700 mt-3 mb-0 font-medium">
                {status.servedHint}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-2 mb-0">
              MedPharma fulfills the medication order and updates this status as the order moves
              from payment to delivery.
            </p>
          </div>

          <div className="p-6">
            {data.image && (
              <div className="mb-6">
                <p className="text-xs font-medium text-gray-500 mb-2">Prescription image</p>
                <Image
                  width="100%"
                  src={data.image}
                  alt=""
                  className="rounded-lg object-cover max-h-64"
                />
              </div>
            )}

            {data.doctorOrderDetails && data.doctorOrderDetails.length > 0 && (
              <div className="mb-6">
                <p className="text-xs font-medium text-gray-500 mb-2">
                  Medications (order line items)
                </p>
                <Table<DoctorPrescriptionOrderDetailItem>
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={medicationColumns}
                  dataSource={data.doctorOrderDetails}
                  scroll={{ x: "max-content" }}
                  aria-label="Medication line items for this prescription"
                />
              </div>
            )}

            <Descriptions
              column={1}
              bordered
              size="small"
              className="rounded-lg overflow-hidden"
              labelStyle={{ background: "#fafafa", fontWeight: 500, width: 140 }}
            >
              <Descriptions.Item label="Patient name">
                {data.patientName}
              </Descriptions.Item>
              <Descriptions.Item label="Contact">
                {data.patientContact}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {data.patientEmail ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Delivery address">
                {data.location ?? "—"}
              </Descriptions.Item>
              {(data.insurance || data.policyNumber) && (
                <>
                  <Descriptions.Item label="Insurance provider">
                    {data.insurance ?? "—"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Policy number">
                    {data.policyNumber ?? "—"}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="Prescribing doctor">
                {data.name ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Doctor contact">
                {data.contact ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Facility">
                {data.institution ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Submitted">
                {data.timestamp
                  ? new Date(data.timestamp).toLocaleString()
                  : "—"}
              </Descriptions.Item>
              {data.paymentStatus && (
                <Descriptions.Item label="Payment status">
                  {data.paymentStatus}
                </Descriptions.Item>
              )}
              {data.notes && (
                <Descriptions.Item label="Clinical / delivery notes">
                  <pre className="whitespace-pre-wrap text-sm font-sans m-0 leading-relaxed">
                    {data.notes}
                  </pre>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        </Card>
      </div>
    </div>
  );
}
